import {ListType} from '@frontapp/editor-html';
import {isUndefined, pick} from 'lodash';
import React, {Component, createRef, ReactNode, RefObject, SyntheticEvent} from 'react';
import {distinctUntilChanged, map, Subscription} from 'rxjs';

import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {trySetLinkUrlFromSelectedText} from '../../../../helpers/editorHelpers';
import {getFileContentType} from '../../../../helpers/fileHelpers';
import {ContentEditableHtmlEditor} from '../../../common/editor/html/contentEditableHtmlEditor';
import {HtmlEditor} from '../../../common/editor/html/htmlEditor';
import {ServiceSubscription} from '../../../common/editor/serviceSubscription';
import {HtmlStyle} from '../../../common/editor/style/htmlStyle';
import {applyFormattingToEditor} from './applyFormatting';
import {HtmlFormattingControls} from './formattingConstants';
import {FormattingImageDialog} from './formattingImageDialog';
import {FormattingLinkDialog} from './formattingLinkDialog';
import {FormattingToolbar} from './formattingToolbar';

/*
 * Props.
 */

interface FormattingToolbarContainerProps {
  isVisible: boolean;
  isSimplified: boolean;
  isKeyboardShortcutsEnabled: boolean;
  htmlEditor: ContentEditableHtmlEditor;
  onImageUpload: (file: File) => void;
  onSaveDefaultStyleClick: () => void;
  renderMoreDropdownItems?: () => ReactNode;
  hasListAutoFormatting: boolean;
  toggleListAutoFormatting: () => void;
  hasRepeatFormatting: boolean;
  isDisabled?: boolean;
}

/*
 * Component.
 */

interface FormattingToolbarContainerState {
  /** HTML editor reference that is stable during async interactions (i.e. while a dialog is open). */
  htmlEditor: ContentEditableHtmlEditor;
  isLinkDialogOpen: boolean;
  isImageDialogOpen: boolean;
  fileUploadInputKey: number;
}

type FormattingOperation = (htmlEditor: HtmlEditor) => void;

export class FormattingToolbarContainer extends Component<
  FormattingToolbarContainerProps,
  FormattingToolbarContainerState
> {
  constructor(props: FormattingToolbarContainerProps) {
    super(props);

    this.fileInputRef = createRef<HTMLInputElement>();

    this.state = {
      htmlEditor: props.htmlEditor,
      isLinkDialogOpen: false,
      isImageDialogOpen: false,
      fileUploadInputKey: 0,
    };
  }

  private readonly fileInputRef: RefObject<HTMLInputElement>;
  private lastFormattingOperation: FormattingOperation | undefined;
  private lastFormattingChainText: string | undefined;

  /** Convenience helper that performs a formatting action and focuses the editor. */
  private applyFormatting(operation: FormattingOperation, {isRepeatable}: {isRepeatable: boolean}) {
    applyFormattingToEditor({
      operation,
      isRepeatable,
      lastFormattingChainText: this.lastFormattingChainText,
      lastFormattingOperation: this.lastFormattingOperation,
      hasRepeatFormatting: this.props.hasRepeatFormatting,
      htmlEditor: this.state.htmlEditor,
    });
  }

  private applyLastFormatting() {
    if (!this.props.hasRepeatFormatting) {
      return;
    }

    if (!this.lastFormattingOperation) {
      return;
    }

    this.endLastFormattingChain();
    this.applyFormatting(this.lastFormattingOperation, {isRepeatable: true});

    trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_REPEAT_CLICK, {
      is_keyboard_shortcut: true,
    });
  }

  private clearLastFormatting() {
    this.lastFormattingOperation = undefined;
    this.lastFormattingChainText = undefined;
  }

  private endLastFormattingChain() {
    this.lastFormattingChainText = undefined;
  }

  /*
   * Event handlers.
   */

  private readonly onStyleChange: (style: HtmlStyle) => void = (newStyle) =>
    this.applyFormatting((htmlEditor) => htmlEditor.services.htmlStyle.applyStyle(newStyle), {
      isRepeatable: true,
    });

  private readonly onIndentButtonClick: () => void = () =>
    this.applyFormatting((htmlEditor) => htmlEditor.services.indent.indent(), {isRepeatable: true});

  private readonly onOutdentButtonClick: () => void = () =>
    this.applyFormatting((htmlEditor) => htmlEditor.services.indent.outdent(), {isRepeatable: true});

  private readonly onListTypeChange: (arg: ListType | undefined) => void = (newListType) =>
    this.applyFormatting((htmlEditor) => htmlEditor.services.list.setType(newListType), {isRepeatable: true});

  private readonly onLinkButtonClick: () => void = () => {
    if (trySetLinkUrlFromSelectedText(this.state.htmlEditor.services.link)) {
      this.clearLastFormatting();
      return;
    }

    this.setState({isLinkDialogOpen: true});
  };

  private readonly onLinkUrlChange: (arg: string | undefined) => void = (newUrl) =>
    this.applyFormatting((htmlEditor) => htmlEditor.services.link.setUrl(newUrl), {isRepeatable: false});

  private readonly onImageUrlChange: (arg: string | undefined) => void = (newUrl) =>
    this.applyFormatting((htmlEditor) => htmlEditor.services.image.setUrl(newUrl), {isRepeatable: false});

  private readonly onUploadImageInlineClick: () => void = () => {
    if (!this.fileInputRef.current) {
      return;
    }

    this.fileInputRef.current.click();
  };

  private readonly onFileInputChange: (arg: SyntheticEvent<HTMLInputElement>) => void = (event) => {
    const {files} = event.currentTarget;
    const fileList = files && Array.from(files);
    if (fileList && fileList.length > 0) {
      fileList.forEach(
        // Only upload the images
        (file) => getFileContentType(file).startsWith('image/') && this.props.onImageUpload(file),
      );
    }

    // Re-create the input to avoid existing-value issues.
    this.setState((state) => ({fileUploadInputKey: state.fileUploadInputKey + 1}));
  };

  private readonly onQuoteButtonClick: (arg: boolean) => void = (newIsQuoted: boolean) =>
    this.applyFormatting(
      (htmlEditor) => {
        const service = htmlEditor.services.quote;
        return newIsQuoted ? service.quote() : service.unquote();
      },
      {isRepeatable: true},
    );

  private readonly onResetStyleClick: () => void = () =>
    this.applyFormatting((htmlEditor) => htmlEditor.services.style.resetStyle(), {isRepeatable: true});

  private htmlEditorSubscription: Subscription | undefined;

  private subscribeToHtmlEditor(htmlEditor: ContentEditableHtmlEditor) {
    this.htmlEditorSubscription?.unsubscribe();

    const subscription = new Subscription();
    // Repeat the last formatting operation.
    subscription.add(
      htmlEditor.services.undo.repeats$.subscribe(() => {
        this.applyLastFormatting();
      }),
    );
    // Whenever the selected text changes, end the chain of remembered formatting operations.
    subscription.add(
      htmlEditor.services.selection.changes$
        .pipe(
          map(() => htmlEditor.services.selection.selectedText),
          distinctUntilChanged(),
        )
        .subscribe(() => {
          this.endLastFormattingChain();
        }),
    );
    this.htmlEditorSubscription = subscription;
  }

  private unsubscribeFromHtmlEditor() {
    this.htmlEditorSubscription?.unsubscribe();
    this.htmlEditorSubscription = undefined;
  }

  /*
   * Lifecycle.
   */

  static getDerivedStateFromProps(
    props: Readonly<FormattingToolbarContainerProps>,
    state: Readonly<FormattingToolbarContainerState>,
  ): Partial<FormattingToolbarContainerState> | null {
    // Keep the HTML editor unchanged while one of the dialogs is open.
    if (state.isLinkDialogOpen || state.isImageDialogOpen) {
      return null;
    }

    const {htmlEditor} = props;
    return {htmlEditor};
  }

  componentDidMount(): void {
    this.subscribeToHtmlEditor(this.state.htmlEditor);
  }

  componentDidUpdate(
    _prevProps: Readonly<FormattingToolbarContainerProps>,
    prevState: Readonly<FormattingToolbarContainerState>,
  ): void {
    if (prevState.htmlEditor !== this.state.htmlEditor) {
      this.unsubscribeFromHtmlEditor();
      this.subscribeToHtmlEditor(this.state.htmlEditor);
    }
  }

  componentWillUnmount(): void {
    this.unsubscribeFromHtmlEditor();
  }

  /*
   * Render.
   */

  render() {
    const {
      isVisible,
      isKeyboardShortcutsEnabled,
      onSaveDefaultStyleClick,
      renderMoreDropdownItems,
      hasListAutoFormatting,
      toggleListAutoFormatting,
      isSimplified,
    } = this.props;
    const {htmlEditor} = this.state;
    const services = pick(htmlEditor.services, ['htmlStyle', 'link', 'image', 'indent', 'quote', 'list']);

    return (
      <>
        <ServiceSubscription source={services}>
          {({
            htmlStyle: {style},
            link: {url: linkUrl},
            image: {url: imageUrl},
            indent: {canIndent, canOutdent},
            quote: {isQuoted},
            list: {type: listType},
          }) => (
            <FormattingToolbar
              isFloating={isSimplified}
              isVisible={isVisible}
              isSimplified={isSimplified}
              isKeyboardShortcutsEnabled={isKeyboardShortcutsEnabled}
              htmlStyle={style}
              onStyleChange={this.onStyleChange}
              canIndent={canIndent}
              canOutdent={canOutdent}
              onIndentButtonClick={this.onIndentButtonClick}
              onOutdentButtonClick={this.onOutdentButtonClick}
              listType={listType}
              onListTypeChange={this.onListTypeChange}
              isLinkButtonActive={!isUndefined(linkUrl)}
              onLinkButtonClick={this.onLinkButtonClick}
              isImageSelectedInComposer={!isUndefined(imageUrl)}
              onInsertImageUrlInlineClick={() => this.setState({isImageDialogOpen: true})}
              onUploadImageInlineClick={this.onUploadImageInlineClick}
              isQuoteButtonActive={isQuoted}
              onQuoteButtonClick={this.onQuoteButtonClick}
              onResetStyleClick={this.onResetStyleClick}
              onSaveDefaultStyleClick={onSaveDefaultStyleClick}
              renderMoreDropdownItems={renderMoreDropdownItems}
              hasListAutoFormatting={hasListAutoFormatting}
              toggleListAutoFormatting={toggleListAutoFormatting}
              controlsToRender={new Set(HtmlFormattingControls)}
              shouldRenderDropdowns
              isDisabled={this.props.isDisabled}
            />
          )}
        </ServiceSubscription>
        {this.maybeRenderLinkDialog()}
        {this.maybeRenderImageDialog()}
        {isVisible && (
          <VisuallyHidden>
            <input
              ref={this.fileInputRef}
              key={this.state.fileUploadInputKey}
              type="file"
              accept="image/*"
              onChange={this.onFileInputChange}
              multiple
            />
          </VisuallyHidden>
        )}
      </>
    );
  }

  // Render dialogs
  private maybeRenderLinkDialog() {
    if (!this.state.isLinkDialogOpen) {
      return null;
    }

    const {htmlEditor} = this.state;
    const {url, selectedText} = htmlEditor.services.link;

    return (
      <FormattingLinkDialog
        link={url}
        onLinkChange={this.onLinkUrlChange}
        onRequestClose={() => this.setState({isLinkDialogOpen: false})}
        selectedText={selectedText}
      />
    );
  }

  private maybeRenderImageDialog() {
    if (!this.state.isImageDialogOpen) {
      return null;
    }

    const {htmlEditor} = this.state;
    const {url} = htmlEditor.services.image;

    return (
      <FormattingImageDialog
        url={url}
        onUrlChange={this.onImageUrlChange}
        onRequestClose={() => this.setState({isImageDialogOpen: false})}
      />
    );
  }
}
